import { IconEye, IconEyeOff, IconLoader } from "@tabler/icons-react";
import {
  type InputHTMLAttributes,
  type ReactNode,
  forwardRef,
  useState,
} from "react";

export interface InputTextProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Input label */
  label?: string;
  /** Error message (displayed in red) */
  errorMessage?: string | boolean;
  /** Informational message */
  infoMessage?: string;
  /** Success message (displayed in green) */
  successMessage?: string;
  /** Display a loading spinner */
  isLoading?: boolean;
  /** Indicates that the input is of type password */
  isPassword?: boolean;
  /** Use search style */
  isSearch?: boolean;
  /** Display the optional text next to the label */
  isOptional?: boolean;
  /** Text to display when the input is optional (default: "(optional)") */
  optionalLabel?: string;
  /** Icon displayed on the left side */
  prefixIcon?: ReactNode;
  /** Text or component displayed on the right side (when not loading) */
  suffix?: string | ReactNode;
  /** Additional icon displayed alongside the label */
  iconLabel?: ReactNode;
  /** Class to define the container's maximum width (default is 'max-w-xl') */
  maxWidth?: string;
  /** Text for the aria-label when showing the password (default: "Show password") */
  showPasswordLabel?: string;
  /** Text for the aria-label when hiding the password (default: "Hide password") */
  hidePasswordLabel?: string;

  /** Button at the en with action */
  button?: ReactNode;
  /** Whether the button is disabled */
  isDisabledButton?: boolean;
  /** Action to be performed when the button is clicked */
  onClickButton?: () => void;
}

export const InputText = forwardRef<HTMLInputElement, InputTextProps>(
  (
    {
      label,
      errorMessage,
      infoMessage,
      successMessage,
      isLoading = false,
      isPassword = false,
      isSearch = false,
      isOptional = false,
      optionalLabel = "(optional)",
      prefixIcon,
      suffix,
      iconLabel,
      maxWidth = "max-w-xl",
      showPasswordLabel = "Show password",
      hidePasswordLabel = "Hide password",
      type = "text",
      className = "",
      button,
      isDisabledButton = false,
      onClickButton,
      ...rest
    },
    ref
  ) => {
    const [isVisible, setIsVisible] = useState(!isPassword);

    // Determine the input type (handles password visibility toggle)
    const inputType = isPassword
      ? isVisible
        ? "text"
        : "password"
      : type;

    // Conditional classes for border and focus ring based on error or success messages
    const borderColor = errorMessage
      ? "border-red-500"
      : successMessage
      ? "border-green-600"
      : "border-black/20";

    const focusColor = errorMessage
      ? "focus:ring-red-500"
      : successMessage
      ? "focus:ring-green-500"
      : "focus:ring-green-500";

    // Add left padding if a prefix icon is present
    const paddingLeft = prefixIcon ? "pl-12" : "";

    const baseClasses = `outline-none placeholder:text-zinc-400 text-zinc-700 dark:text-zinc-100 w-full h-12 bg-white dark:bg-white/10 rounded border px-5 dark:border-white/30 ${paddingLeft} ${
      isSearch ? "!pl-10 text-sm dark:border-transparent md:text-base" : ""
    }`;

    return (
      <div className={`w-full ${maxWidth} flex flex-col gap-2 transition-all`}>
        {label && (
          <label
            htmlFor={rest.id || rest.name?.toString()}
            className="dark:text-white/80 text-zinc-600 text-base font-medium flex items-center"
          >
            {label}
            {isOptional && (
              <span className="text-black/40 dark:text-white/60 ml-1">
                {optionalLabel}
              </span>
            )}
            {iconLabel}
          </label>
        )}

        <div className="relative w-full">
          <div className="flex items-center">
            <input
              id={rest.id || rest.name?.toString()}
              ref={ref}
              {...rest}
              type={inputType}
              className={`${baseClasses} ${borderColor} ${focusColor} focus:ring-1 ${className} ${
                button ? "rounded-tr-none rounded-br-none" : ""
              }`}
              aria-invalid={!!errorMessage}
            />
            {suffix}
            {button && (
              <div
                onClick={isDisabledButton ? undefined : onClickButton}
                className={`h-12 px-3 text-white transition-all aspect-square rounded-tr rounded-br  flex items-center justify-center ${
                  isDisabledButton
                    ? "cursor-not-allowed bg-green-400"
                    : "cursor-pointer bg-green-500"
                }`}
              >
                {button}
              </div>
            )}
          </div>

          {prefixIcon && (
            <div className="absolute inset-y-0 left-3 flex items-center text-zinc-500 dark:text-zinc-300">
              {prefixIcon}
            </div>
          )}

          {isLoading && (
            <div className="absolute inset-y-0 right-3 flex items-center text-zinc-400">
              <IconLoader className="animate-spin animate-duration-[2s]" />
            </div>
          )}

          {isPassword && (
            <button
              type="button"
              onClick={() => setIsVisible(!isVisible)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2"
              aria-label={isVisible ? hidePasswordLabel : showPasswordLabel}
            >
              {isVisible ? (
                <IconEyeOff
                  className="text-zinc-400 cursor-pointer"
                  strokeWidth={1.5}
                />
              ) : (
                <IconEye
                  className="text-zinc-400 cursor-pointer"
                  strokeWidth={1.5}
                />
              )}
            </button>
          )}
        </div>

        <div className="-mt-1 flex flex-col">
          {errorMessage ? (
            <span
              className="text-red-500 dark:text-red-400 font-medium text-sm animate-fade animate-duration-400"
              role="alert"
            >
              {errorMessage}
            </span>
          ) : successMessage ? (
            <span
              className="text-green-600 dark:text-green-400 font-medium text-sm animate-fade animate-duration-400"
              role="status"
            >
              {successMessage}
            </span>
          ) : infoMessage ? (
            <span className="text-zinc-500 dark:text-zinc-300 font-medium text-sm animate-fade animate-duration-400">
              {infoMessage}
            </span>
          ) : null}
        </div>
      </div>
    );
  }
);

InputText.displayName = "InputText";
